import { CustomButton, BasicNode, ColorizeTreeEvent, FlatNode, ChangeDataEvent, ToggleTreePreloaderEvent } from './types';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { TreePreloaderType } from './basic-tree-tooltip/types';
import { CommonBroadcastService } from '../common-broadcast/common-broadcast.service';
import { EventTypes } from '@caslink/common-client';
import { FlatTreeControl } from '@angular/cdk/tree';
import { isIcon } from './basic-tree-icon/basic-tree-icon.component';
import { isTooltip } from './basic-tree-tooltip/basic-tree-tooltip.component';
import { Subscription } from 'rxjs';
import { ThemeService } from '@caslink/common-client/ng-app/theme/ThemeService';
import * as _ from 'lodash';

@Component({
    selector: 'cl-basic-tree',
    templateUrl: './basic-tree.component.html',
    styleUrls: ['basic-tree.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BasicTreeComponent {
    static $name = 'clBasicTree';

    @Input() displayMultiselect = false;
    @Input() treeData: BasicNode;
    @Input() customButtons: CustomButton[] = [];
    @Input() expandLevels: number;
    @Input() isTreeOpened: boolean;

    @Output() nodeSelectedEvent = new EventEmitter<{node: FlatNode, isMultiselect: boolean}>();
    @Output() customButtonClicked = new EventEmitter<{customButton: any, node: FlatNode}>();

    @ViewChild('tree', {static: false}) tree: ElementRef;

    expandedNodes: FlatNode[] = [];
    isMultiselect = false;
    subscriptions = new Subscription();

    isIcon = isIcon;
    isTooltip = isTooltip;

    _transformer = (node: BasicNode, level: number) => {
        const flatNode: FlatNode = {
            expandable: !!node.items && node.items.length > 0,
            level,
            $hidden: false,
            $highlightedPath: false,
            $selected: false,
            ...node,
        }
        return flatNode;
    };

    treeControl = new FlatTreeControl<FlatNode>(
        node => node.level,
        node => node.expandable,
    );

    treeFlattener = new MatTreeFlattener(
        this._transformer,
        node => node.level,
        node => node.expandable,
        node => node.items,
    );

    dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    constructor(
        private cdRef: ChangeDetectorRef,
        private commonBroadcastService: CommonBroadcastService,
        private themeService: ThemeService,
    ) {}

    ngOnDestroy() {
		this.endSubscriptions();
	}

    ngOnChanges(changes: SimpleChanges) {

        if (changes.treeData) {
            this.endSubscriptions();
            this.startSubscriptions();
            this.saveExpandedNodes();
            this.createPathToNodes(changes.treeData.currentValue);
            this.dataSource.data = [];
            this.dataSource.data = [changes.treeData.currentValue];
            this.expandNodeAfterDataLoad(changes.treeData.firstChange);
            this.cdRef.detectChanges();
            this.notifyWhenTreeIsFullyRendered();
        }

        if (changes?.isTreeOpened?.currentValue) {
            this.scrollIntoNode();
        }
    }

    private createPathToNodes(node: BasicNode, parentPath?: string) {
        node.$currentPath = `${parentPath ? `${parentPath}>` : '' }${node.name}`;
        node.$parentPath = parentPath ?? '';

        for (const child of node?.items ?? []) {
            this.createPathToNodes(child, node.$currentPath);
        }
    }

    private notifyWhenTreeIsFullyRendered() {
        this.commonBroadcastService.broadcast(EventTypes.TreeIsReady);
    }

    private endSubscriptions() {
        this.subscriptions.unsubscribe();
        this.subscriptions = new Subscription();
    }

    private startSubscriptions() {
        this.subscribeToNodeSelection();
        this.subscribeToHideTreeNodesFilter();
        this.subscribeToNodeColorChange();
        this.subscribeToNodeColorReset();
        this.subscribeToDataPreloaderToggle();
    }

    private scrollIntoNode() {
        setTimeout(() => {
            const element = this.tree.nativeElement.getElementsByClassName('node__selected')[0];
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
            }
        }, 0);
    }

    private saveExpandedNodes() {
        this.expandedNodes = [];

        for (const node of this.treeControl?.dataNodes ?? []) {
            if (node.expandable && this.treeControl.isExpanded(node)) {
                this.expandedNodes.push(node);
            }
        }
    }

    private restoreExpandedNodes() {
        for (const node of this.expandedNodes) {
            const foundNode = this.findNodeByPath(node.$currentPath);

            if (foundNode) {
                this.treeControl.expand(foundNode);
            }
        }
    }

    private expandNodeAfterDataLoad(firstChange: boolean) {

        if (firstChange && this.expandLevels !== undefined) {
            this.expandNodesToLevel(this.expandLevels);
        }

        if (!firstChange) {
            this.restoreExpandedNodes();
        }
    }

    private expandNodesToLevel(level: number) {
        for (const node of this.treeControl.dataNodes) {
            if (node?.level <= level) {
                this.treeControl.expand(node);
            }
        }
    }

    private expandParents(childNode: BasicNode) {
        if (childNode?.$parentPath) {
            const parentNode = this.findNodeByPath(childNode?.$parentPath);
            if (parentNode) {
                this.treeControl.expand(parentNode);
                this.expandParents(parentNode);
            }
        }
    }

    private findNodeByPath(path: string) {
        return this.treeControl.dataNodes.find(node => node.$currentPath === path);
    }

    private deselectNode(node: FlatNode) {
        const result = this.treeControl.dataNodes.find(selectedNode => selectedNode === node);
        if (result) {
            result.$selected = false;
            this.nodeSelectedEvent.emit({ node, isMultiselect: this.isMultiselect });
        }
    }

    private resetSelectedAttribute() {
        for (const node of this.treeControl.dataNodes) {
            node.$selected = false;
        }
    }

    onNodeSelection(node: FlatNode, emitEvent = false) {
        const expandableNodesRestriction = this.isMultiselect && node.expandable;
        const shouldDeselectNodeRestriction = this.isMultiselect && node.$selected;

        if (expandableNodesRestriction) {
            return;
        }

        if (shouldDeselectNodeRestriction) {
            this.deselectNode(node);
            return;
        }

        if (!this.isMultiselect) {
            this.resetSelectedAttribute();
            this.resetTreePath();
        }

        node.$selected = true;

        this.treeControl.expand(node);
        this.setTreePath(node);
        this.expandParents(node);

        if (emitEvent) {
            this.nodeSelectedEvent.emit({ node, isMultiselect: this.isMultiselect });
        }
        this.cdRef.detectChanges();
    }

    toggleMultiselect() {
        this.isMultiselect = !this.isMultiselect;
    }

    private resetTreePath() {
        for (const node of this.treeControl.dataNodes) {
            node.$highlightedPath = false;
        }
    }

    private setTreePath(node: FlatNode) {
        node.$highlightedPath = true;
        const parent = this.findNodeByPath(node?.$parentPath);

        if (parent) {
            this.setTreePath(parent);
        }
    }

    private colorizeParents(childNode: FlatNode, colorClass: string) {
        if (childNode?.$parentPath) {
            const parentNode = this.findNodeByPath(childNode?.$parentPath);
            if (parentNode) {
                parentNode.$color = colorClass;
                this.colorizeParents(parentNode, colorClass);
            }
        }
    }

    private changeNodeColor(event: ColorizeTreeEvent) {
        const foundNode = this.findNodeByPath(event.node.$currentPath);

        if (foundNode) {
            foundNode.$color = event.colorClass;
        }

        if (foundNode && event.colorizeParents) {
            this.colorizeParents(foundNode, event.colorClass);
        }
    }

    private resetAllNodeColors() {
        for (const node of this.treeControl.dataNodes) {
            node.$color = undefined;
        }
        this.cdRef.detectChanges();
    }

    private subscribeToDataPreloaderToggle() {
        this.subscriptions.add(this.commonBroadcastService.on<ToggleTreePreloaderEvent>(EventTypes.ToggleTreePreloader)
            .subscribe((event: ToggleTreePreloaderEvent) => {
                this.removePreloaders();
                this.setPreloader(event);
            })
        );
    }

    private removePreloaders() {
        for (const node of this.treeControl.dataNodes) {
            node.$isPreloader = undefined;
            node.$preloaderType = undefined;
        }
        this.cdRef.detectChanges();
    }

    private setPreloader(event: ToggleTreePreloaderEvent) {
        const findParentNodeFilter = (node: FlatNode) => node?.category === event.device.category;
        const findNodeFilter = (node: FlatNode) => node?.category === event.device.category && node?.hardwareItemId === event.device.hardwareItemId;

        const filter = event.preloaderType === TreePreloaderType.ADD_DEVICE ? findParentNodeFilter : findNodeFilter;

        const node = this.treeControl.dataNodes.find(filter);
        if (node) {
            node.$isPreloader = true;
            node.$preloaderType = event.preloaderType;
            this.cdRef.detectChanges();
        }
    }

    private subscribeToNodeColorReset() {
        this.subscriptions.add(this.commonBroadcastService.on(EventTypes.ResetAllTreeNodesColor)
            .subscribe(() => {
                this.resetAllNodeColors();
            })
        );
    }

    private subscribeToNodeColorChange() {
        this.subscriptions.add(this.commonBroadcastService.on<ColorizeTreeEvent>(EventTypes.SetTreeNodeColor)
            .subscribe((event: ColorizeTreeEvent) => {
                this.changeNodeColor(event);
                this.cdRef.detectChanges();
            })
        );
    }

    private subscribeToNodeSelection() {
        this.subscriptions.add(this.commonBroadcastService.on<ChangeDataEvent>(EventTypes.SetTreeNodeActive)
            .subscribe((event: ChangeDataEvent) => {
                this.selectNodeByFilter(event);
            })
        );
    }

    private subscribeToHideTreeNodesFilter() {
        this.subscriptions.add(this.commonBroadcastService.on<ChangeDataEvent>(EventTypes.HideTreeNodes)
            .subscribe((event: ChangeDataEvent) => {
                this.hideNodeByFilter(event);
                this.cdRef.detectChanges();
            })
        )
    }

    private hideNodeByFilter(event: ChangeDataEvent) {
        for (const node of this.treeControl.dataNodes) {
            node.$hidden = false;
            if (event.applyFilter && event.filter(node)) {
                node.$hidden = true;
            }
        }
    }

    private selectNodeByFilter(event: ChangeDataEvent) {
        const node = this.treeControl.dataNodes.find(event.filter);
        if (node) {
            this.onNodeSelection(node, event.emitNodeSelected);
            this.scrollIntoNode();
            this.cdRef.detectChanges();
        }
    }

    isDarkTheme() {
        return this.themeService.getTheme() === 'casDark';
    }

    hasChild = (_: number, node: FlatNode) => {
        return node.expandable;
    };
}
